import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ArrowRightIcon, ListChecks } from 'lucide-react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useNavigate, Link } from 'react-router';

import { TaskStatusEnum } from 'src/graphql/schema';
import { MarkAsReadTasksDocument } from 'src/graphql/actions/tasks/markAsReadTasks.mutation';
import { TasksDocument, type ITasksQuery } from 'src/graphql/actions/tasks/tasks.query';
import { EmptyBlock } from '@/components/ui/empty-block';
import { withSuspense } from 'src/hoc/withSuspense';
import { ClientError, getApiError } from 'src/utils/errors';
import { cn } from 'src/lib/utils';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import {
  CriticalityTooltip,
  TypeTooltip,
  useRefetchTasks,
  getTaskContent,
  eventTypeKeys,
  setTaskUrl,
} from 'src/containers/Tasks';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

dayjs.extend(relativeTime);

type ITaskNode = ITasksQuery['tasks']['nodes'][number];

const columnHelper = createColumnHelper<ITaskNode>();

const columns = [
  columnHelper.accessor('eventType', {
    cell: (props) => {
      const parentType = props.row.original.parentType;
      const payload = props.row.original.payload;
      const content = getTaskContent({
        eventType: props.getValue() as (typeof eventTypeKeys)[number],
        payload,
        parentType,
      });

      if (!content) return null;

      const { title, description, icon: Icon } = content;

      return (
        <div className="flex items-start gap-2">
          {Icon && (
            <TypeTooltip type={parentType === 'Escrow' ? payload.escrow_type : parentType}>
              <Icon className="mt-[5px] size-4.5 shrink-0" />
            </TypeTooltip>
          )}
          <div className="text-foreground grow">
            <span className="text-sm font-medium">{title}</span>
            <p className="text-xs">{description}</p>
          </div>
        </div>
      );
    },
    meta: {
      headerClassName: 'min-w-60',
      cellClassName: 'min-w-60',
    },
  }),
  columnHelper.accessor('criticality', {
    cell: (props) => <CriticalityTooltip type={props.getValue()} />,
    meta: {
      headerClassName: 'w-8',
      cellClassName: 'w-8 px-0 text-center',
    },
  }),
  columnHelper.accessor('createdAt', {
    cell: (props) => {
      const date = dayjs(props.getValue());
      const formatted = dayjs().diff(date, 'day') > 7 ? date.format('MMM D, YYYY') : date.fromNow();
      return <span className="text-sm text-foreground whitespace-nowrap">{formatted}</span>;
    },
    meta: {
      cellClassName: 'text-right',
    },
  }),
];

const UnreadTasksContainer = () => {
  const refetchTasks = useRefetchTasks();

  const { data } = useSuspenseQuery(TasksDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      status: TaskStatusEnum.Unread,
      page: 1,
      perPage: 10,
    },
  });

  const [markAsRead, { loading: isLoading }] = useMutation(MarkAsReadTasksDocument);

  const handleMarkAsRead = async (taskIds: string[]) => {
    try {
      const { data } = await markAsRead({ variables: { taskIds } });

      if (data?.markAsReadTasks?.success) {
        refetchTasks();
        toast.success('Task successfully marked as read');
      } else {
        throw new ClientError(data?.markAsReadTasks?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const navigate = useNavigate();

  const table = useReactTable({
    data: data.tasks.nodes,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const onRowClick =
    ({ id, ...rest }: { id: string } & Parameters<typeof setTaskUrl>[0]) =>
    () => {
      handleMarkAsRead([id]);
      const { path, state } = setTaskUrl(rest);
      navigate(path, { state });
    };

  const noData = !data.tasks.nodesCount;

  return (
    <Card>
      <CardHeader className="pb-4 space-y-0 gap-2 sm:items-center sm:flex-row sm:justify-between">
        <CardTitle className="text-lg">Unread tasks</CardTitle>
        {!noData && (
          <Button asChild variant="outline">
            <Link to="/tasks">
              View all <ArrowRightIcon />
            </Link>
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {noData ? (
          <EmptyBlock
            icon={ListChecks}
            title="You are up to date"
            classes={{
              title: 'text-sm',
              content: 'h-77.5 flex flex-col justify-center',
            }}
            withCard={false}
          />
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <Table isLoading={isLoading}>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={onRowClick({
                      id: row.original.id,
                      parentId: row.original.parentId,
                      parentType: row.original.parentType,
                      eventType: row.original.eventType,
                      payload: row.original.payload,
                      recordId: row.original.recordId,
                    })}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className={cn(cell.column.columnDef.meta?.cellClassName)}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default withSuspense(UnreadTasksContainer);
